/**
 * The permission model. See CLAUDE.md 3.5 and SECURITY.md section 3.
 *
 * Two independent axes. They are deliberately NOT collapsed into one scale, and the check
 * requires both to pass. Phase 1 defines the types and the check; Phase 2 wires them into the
 * agent runtime. The check is enforced in the runtime, never in a prompt.
 */
import { z } from 'zod';
/** Axis 1 — how much an agent is allowed to do on its own. Set per agent. */
export var AutonomyLevel;
(function (AutonomyLevel) {
    /** Retrieve data. Cannot draft, recommend, or act. */
    AutonomyLevel[AutonomyLevel["ReadOnly"] = 0] = "ReadOnly";
    /** Produce drafts and recommendations. Cannot submit actions for execution. */
    AutonomyLevel[AutonomyLevel["DraftRecommend"] = 1] = "DraftRecommend";
    /** Submit action requests to the Approval Center. Nothing executes without human approval. */
    AutonomyLevel[AutonomyLevel["ProposeActions"] = 2] = "ProposeActions";
    /** Execute LOW-risk actions without approval, subject to flags, thresholds, and kill switch. */
    AutonomyLevel[AutonomyLevel["LimitedAutonomy"] = 3] = "LimitedAutonomy";
})(AutonomyLevel || (AutonomyLevel = {}));
/** Axis 2 — how dangerous an action is. Set per action type, independent of the requester. */
export const RISK_CLASSES = ['LOW', 'MEDIUM', 'HIGH'];
export const riskClassSchema = z.enum(RISK_CLASSES);
/**
 * HIGH-risk actions, at minimum. CLAUDE.md 3.5.
 *
 * These are NEVER eligible for autonomous execution, regardless of agent level, confidence, or
 * feature flag. There is no override.
 */
export const ALWAYS_HIGH_RISK_ACTIONS = [
    'refund',
    'payment',
    'bank_transfer',
    'owner_distribution',
    'accounting_journal_entry',
    'legal_communication',
    'contract_signing',
    'major_purchase',
    'delete_important_data',
    'change_banking_information',
    'material_pricing_change',
    'tax_filing',
];
/**
 * The permission check.
 *
 * Order matters: the kill switch vetoes first and unconditionally, then the grant, then the two
 * axes. HIGH risk always requires approval, and that branch is unreachable by any flag.
 */
export function checkPermission(req) {
    if (req.killSwitchActive) {
        return { decision: 'refuse', reason: 'Kill switch is active (SECURITY.md 6).' };
    }
    if (!req.toolGranted) {
        return {
            decision: 'refuse',
            reason: 'This tool is not granted to this agent (SECURITY.md 3.5).',
        };
    }
    // Axis 1: what may this agent do at all?
    if (req.agentAutonomy === AutonomyLevel.ReadOnly) {
        return {
            decision: 'refuse',
            reason: 'Agent is READ ONLY (level 0): it cannot draft, recommend, or act.',
        };
    }
    if (req.agentAutonomy === AutonomyLevel.DraftRecommend) {
        return {
            decision: 'refuse',
            reason: 'Agent is DRAFT / RECOMMEND (level 1): it cannot submit actions for execution. ' +
                'Its output is a recommendation, not an action request.',
        };
    }
    // Axis 2: what does this action require, regardless of who asked?
    if (req.actionRisk === 'HIGH') {
        return {
            decision: 'require_approval',
            reason: 'HIGH-risk actions always require explicit human approval and are never eligible for ' +
                'autonomous execution, regardless of agent level, confidence, or feature flag ' +
                '(CLAUDE.md 3.5).',
        };
    }
    if (req.actionRisk === 'MEDIUM') {
        return {
            decision: 'require_approval',
            reason: 'MEDIUM-risk actions require human approval.',
        };
    }
    // LOW risk. Level 2 still routes through approval; only Level 3 executes directly.
    if (req.agentAutonomy === AutonomyLevel.ProposeActions) {
        return {
            decision: 'require_approval',
            reason: 'Agent is PROPOSE ACTIONS (level 2): nothing executes without human approval.',
        };
    }
    // Level 3 + LOW risk. Still gated by the per-pair autonomy flag (Phase 12).
    if (!req.autonomyFlagEnabled) {
        return {
            decision: 'require_approval',
            reason: 'Autonomous execution is not enabled for this agent and action pair ' +
                '(feature flag off — ROADMAP.md Phase 12).',
        };
    }
    return { decision: 'execute' };
}
//# sourceMappingURL=permissions.js.map